import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../ui/dialog';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Legend
} from 'recharts';
import { apiOperatorHistory } from '../../api';
import { Target, TrendingUp, AlertTriangle, Calendar, Euro, Package, Clock } from 'lucide-react';

const fmtEur = (v) => '€' + (v || 0).toFixed(2);
const fmtDate = (iso) => new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });

// Chart constants — extracted to avoid creating new object references on every render
const CHART_MARGIN = { top: 5, right: 20, left: 0, bottom: 5 };
const AXIS_TICK_11 = { fontSize: 11 };
const TOOLTIP_STYLE = { borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 };
const LINE_DOT = { r: 3 };

const OperatorHistory = ({ operator, onClose }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (operator?.id) {
      apiOperatorHistory(operator.id).then(setData).catch(() => setData(null));
    } else {
      setData(null);
    }
  }, [operator]);

  const op = data?.operator;
  const daily = data?.daily || [];
  const target = op?.target_eur_per_hour || 0;

  return (
    <Dialog open={!!operator} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {op ? (
              <>
                <span className="font-mono bg-slate-100 px-2 py-0.5 rounded text-sm">{op.alias}</span>
                <span>{op.first_name} {op.last_name}</span>
              </>
            ) : 'Caricamento...'}
          </DialogTitle>
        </DialogHeader>

        {!data ? (
          <div className="py-10 text-center text-sm text-slate-500">Caricamento storico...</div>
        ) : (
          <div className="space-y-4">
            {/* Below-target alert */}
            {data.below_target_streak >= 3 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-900">
                  <strong>Attenzione:</strong> questo operatore è sotto target da <strong>{data.below_target_streak} giorni consecutivi</strong>
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              <div className="bg-slate-50 rounded-lg px-3 py-2">
                <div className="text-[10px] uppercase text-slate-500 tracking-wide">Target</div>
                <div className="text-lg font-bold text-slate-900 tabular-nums">{target > 0 ? fmtEur(target) : '—'}</div>
              </div>
              <div className="bg-slate-50 rounded-lg px-3 py-2">
                <div className="text-[10px] uppercase text-slate-500 tracking-wide">Giorni lavorati</div>
                <div className="text-lg font-bold text-slate-900 tabular-nums">{daily.length}</div>
              </div>
              <div className="bg-slate-50 rounded-lg px-3 py-2">
                <div className="text-[10px] uppercase text-slate-500 tracking-wide">Sessioni totali</div>
                <div className="text-lg font-bold text-slate-900 tabular-nums">{data.sessions.length}</div>
              </div>
              <div className="bg-slate-50 rounded-lg px-3 py-2">
                <div className="text-[10px] uppercase text-slate-500 tracking-wide">Totale fatturato</div>
                <div className="text-lg font-bold text-blue-700 tabular-nums">{fmtEur(daily.reduce((a, b) => a + b.total_eur, 0))}</div>
              </div>
            </div>

            {/* Chart */}
            {daily.length > 0 ? (
              <div className="bg-white border border-slate-200 rounded-lg p-3">
                <div className="text-sm font-semibold text-slate-900 mb-3">Andamento €/ora</div>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={daily} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={AXIS_TICK_11} stroke="#94a3b8" tickFormatter={(d) => fmtDate(d)} />
                    <YAxis tick={AXIS_TICK_11} stroke="#94a3b8" />
                    <Tooltip
                      contentStyle={TOOLTIP_STYLE}
                      formatter={(v) => [fmtEur(v), '€/ora']}
                      labelFormatter={(d) => fmtDate(d)}
                    />
                    {target > 0 && <ReferenceLine y={target} stroke="#dc2626" strokeDasharray="5 5" label={{ value: `Target ${fmtEur(target)}`, position: 'right', fontSize: 11, fill: '#dc2626' }} />}
                    <Line type="monotone" dataKey="avg_eur_per_hour" stroke="#2563eb" strokeWidth={2} dot={LINE_DOT} name="€/ora" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="py-8 text-center text-sm text-slate-500">Nessun dato disponibile</div>
            )}

            {/* Recent sessions */}
            {data.sessions.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 border-b border-slate-200 text-sm font-semibold">Sessioni recenti</div>
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold text-slate-600">Data</th>
                        <th className="text-left px-3 py-2 font-semibold text-slate-600">Lavorazione</th>
                        <th className="text-right px-3 py-2 font-semibold text-slate-600">Pezzi</th>
                        <th className="text-right px-3 py-2 font-semibold text-slate-600">Ore</th>
                        <th className="text-right px-3 py-2 font-semibold text-slate-600">€/ora</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.sessions.slice().reverse().slice(0, 30).map(s => (
                        <tr key={s.id}>
                          <td className="px-3 py-1.5">{fmtDate(s.date)}</td>
                          <td className="px-3 py-1.5 font-mono">{s.work_type_code}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{s.pieces}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{s.hours.toFixed(1)}h</td>
                          <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${target > 0 ? (s.eur_per_hour >= target ? 'text-emerald-700' : 'text-red-700') : 'text-slate-700'}`}>
                            {fmtEur(s.eur_per_hour)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OperatorHistory;
